import * as React from "react";
import { Slide, ToastContainer, type TypeOptions } from "react-toastify";

import { cn } from "@/lib/utils";
import {
  Notification,
  type NotificationProps,
} from "@/components/ui/notification";

type ToastVariant = NonNullable<NotificationProps["variant"]>;

const toastVariantByType: Record<string, ToastVariant> = {
  default: "default",
  success: "success",
  info: "info",
  warning: "warning",
  error: "error",
};

export const resolveToastVariant = (type?: TypeOptions): ToastVariant => {
  if (!type) {
    return "default";
  }

  return toastVariantByType[type] ?? "default";
};

const ToastStatusIcon = ({ variant }: { variant: ToastVariant }) => {
  if (variant === "success") {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="7.5" />
        <path d="m6.75 10 2.1 2.1 4.4-4.7" />
      </svg>
    );
  }

  if (variant === "error") {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <circle cx="10" cy="10" r="7.5" />
        <path d="m7.25 7.25 5.5 5.5m0-5.5-5.5 5.5" />
      </svg>
    );
  }

  if (variant === "warning") {
    return (
      <svg viewBox="0 0 20 20" aria-hidden="true">
        <path d="M9 3.8a1.15 1.15 0 0 1 2 0l6 10.4A1.15 1.15 0 0 1 16 16H4a1.15 1.15 0 0 1-1-1.8L9 3.8Z" />
        <path d="M10 7.2v4.2m0 2.1v.1" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="10" cy="10" r="7.5" />
      <path d="M10 9v4.2m0-6.8v.1" />
    </svg>
  );
};

const Toast = React.forwardRef<HTMLDivElement, NotificationProps>(
  ({ className, ...props }, ref) => (
    <Notification
      ref={ref}
      className={cn(
        "shadow-[0px_4px_8px_0px_rgba(16,24,40,0.1),0px_2px_4px_0px_rgba(16,24,40,0.06)]",
        className
      )}
      {...props}
    />
  )
);
Toast.displayName = "Toast";

const GlobalToastContainer = () => (
  <ToastContainer
    className="sked-toast__container"
    position="top-center"
    autoClose={3000}
    hideProgressBar
    newestOnTop
    closeOnClick
    transition={Slide}
    closeButton={({ closeToast }) => (
      <button
        type="button"
        aria-label="Dismiss"
        className="sked-toast__close"
        onClick={closeToast}
      >
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="m6 6 8 8m0-8-8 8" />
        </svg>
      </button>
    )}
    pauseOnHover
    toastClassName={(context) =>
      `Toastify__toast sked-toast sked-toast--${resolveToastVariant(context?.type)}`
    }
    bodyClassName="sked-toast__body"
    icon={({ type }) => <ToastStatusIcon variant={resolveToastVariant(type)} />}
    style={{ zIndex: 99_999 }}
  />
);

export { GlobalToastContainer, Toast, type ToastVariant };
